// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { Fragment, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import { arrayOf, bool, func, number, object, shape, string } from 'prop-types';
import {
  Button,
  Grid,
  Link,
  Typography,
  withStyles,
} from '@material-ui/core';
import WarningIcon from "@material-ui/icons/Warning";
import { useIntl } from 'react-intl';
import { cloneDeep, difference, findIndex, each, keys, map, capitalize, isEmpty } from 'lodash';

import {
  FilterByName,
  FilterByAccessType,
} from './controls';
import styles from './styles';
import { AlertDialog, FormActionButtons, Switch } from '../../../../components';
import ListContainer from '../../../list';
import {
  GRID_ROWS_PER_PAGE_OPTIONS,
  GRID_ROWS_PER_PAGE_DEFAULT_OPTION,
  PRODUCT_LIST_ACCESS_TYPE_FILTER_OPTIONS,
  PRODUCT_ACCESS_TYPE_ALL,
  PERMISSION_PRODUCTS_API,
} from '../../../../constants';
import {
  fetchApisProductList,
  updateApisProductList,
} from '../../../../actions/api';
import {
  getIsProductAccessSaveSuccess,
  getProductListTotalPages,
  getProductListTotalElements,
  getProductListResults,
  getErrors,
} from '../../../../reducers/api';
import { getI18n } from '../../../../utils/intl';
import { hasCRUDPermissions } from "../../../../utils";

const CHANGE_MODE_REMOVE_ACCESS = 'CHANGE_MODE_REMOVE_ACCESS';
const CHANGE_MODE_ADD_ACCESS = 'CHANGE_MODE_ADD_ACCESS';

export const Products = (props) => {
  const {
    apiUuid,
    classes,
    fetchList,
    isApiUpdateSuccess,
    notifyMessages,
    rows = [],
    totalElements,
    totalPages,
    updateAccess,
    tabValue,
    errors,
    userContext,
  } = props;

  const { getI18nMessage } = getI18n(useIntl());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(GRID_ROWS_PER_PAGE_DEFAULT_OPTION);
  const [filterByName, setFilterByName] = useState('');
  const [accessTypeFilterValue, setAccessTypeFilterValue] = useState(PRODUCT_ACCESS_TYPE_ALL);
  const [showFormActionBtns, setShowFormActionBtns] = useState(false);
  const [changeMode, setChangeMode] = useState(CHANGE_MODE_REMOVE_ACCESS);
  const [currentProductsRows, setCurrentProductRows] = useState([]);
  const [modifiedRecords, setModifiedRecords] = useState(0);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const hasUpdate = hasCRUDPermissions(userContext, ['UPDATE'], PERMISSION_PRODUCTS_API);
  const emptyFunction = () => {};

  useEffect(() => {
    if (apiUuid && tabValue === 'api-details-products-tab') {
      fetchListWithFilters();
    }
  }, [apiUuid, tabValue]);

  const getListColumns = ({ getI18nMessage }) => [{
    id: 'productName',
    label: getI18nMessage('label.api.details.apiproducts.list.product.title'),
    minWidth: 200,
    format: ({ productName, uuid } = {}) => (<Link underline="none" href={`/admin/products/details/${uuid}`}>
      {productName}
    </Link>),
    value: ({ productName, productUuid } = {}) => ({ productName, uuid: productUuid }),
  },{
    id: 'productAccessStatus',
    label: getI18nMessage('label.access.type'),
    minWidth: 50,
    value: ({ productAccessStatus }) => capitalize(productAccessStatus),
  },{
    id: 'productApiAccessStatus',
    label: getI18nMessage('label.api.details.apiproducts.list.access.title'),
    minWidth: 50,
    format: ({ hasAccess, uuid }) =>
      (<Switch
        id={`product-access-${uuid}`}
        onChange={(val) => onAccessToggle(val, uuid)}
        checked={hasAccess}
        disabled={!hasUpdate}
      />
    ),
    value: ({ productUuid, productApiAccessStatus }) => ({ uuid: productUuid, hasAccess: productApiAccessStatus === 'ASSIGNED' }),
  }];

  useEffect(() => {
    if ((changeMode === CHANGE_MODE_ADD_ACCESS) || isFormDirty) {
      setShowFormActionBtns(true);
    }
    if ((changeMode !== CHANGE_MODE_ADD_ACCESS) && !isFormDirty) {
      setShowFormActionBtns(false);
    }
  }, [changeMode, isFormDirty]);

  useEffect(() => {
    setCurrentProductRows(cloneDeep(rows));
  }, [rows]);

  useEffect(() => {
    if (isApiUpdateSuccess) {
      notifyMessages('success', getI18nMessage('label.product.updated'));
      setChangeMode(CHANGE_MODE_REMOVE_ACCESS);
      setPage(0);
      fetchListWithFilters();
      setTimeout(() => {
        notifyMessages('', '');
      }, 2500);
    }
  }, [isApiUpdateSuccess]);

  const onAccessToggle = (val, uuid) => {
      updateCurrentProductRow(uuid, !!val);
  };

  useEffect(() => {
    const rowsMap = {};
    each(rows, item => {
      rowsMap[item.productUuid] = item;
    });
    const currentProductsRowsMap = {};
    each(currentProductsRows, item => {
      currentProductsRowsMap[item.productUuid] = item;
    });
    const rowIds = keys(rowsMap);
    const currentProductsRowsIds = keys(currentProductsRowsMap);
    if ( difference(rowIds, currentProductsRowsIds).length !== 0) {
      return ;
    }
    let modifiedRecords = 0;
    each(rowIds, id => {
      if (rowsMap[id].productApiAccessStatus !==
        currentProductsRowsMap[id].productApiAccessStatus) {
        modifiedRecords++;
      }
    });
    setModifiedRecords(modifiedRecords);
  }, [currentProductsRows]);

  useEffect(() => {
    if (modifiedRecords > 0) {
      setIsFormDirty(true);
    } else {
      setIsFormDirty(false);
    }
  }, [modifiedRecords]);

  useEffect(() => {
    if(tabValue === 'api-details-products-tab') {
      fetchListWithFilters();
    }
  }, [changeMode]);

  useEffect(() => {
    // check if error is related to prompting user to force remove
    if(!isEmpty(errors)) {
      const ERR_API_IN_USE = "error.product.api.in.use.by.application"
      const error = errors.filter(err => err.key === ERR_API_IN_USE);
      if(!isEmpty(error)) { setIsDialogOpen(true); }
    }
  }, [errors])

  const updateCurrentProductRow = (uuid, access) => {
    const index = findIndex(currentProductsRows, item => item.productUuid === uuid);
    currentProductsRows[index].productApiAccessStatus = access ? 'ASSIGNED' : 'NOT_ASSIGNED';
    setCurrentProductRows([...currentProductsRows]);
  };

  const fetchListWithFilters = ({
    hasAccess = changeMode === CHANGE_MODE_REMOVE_ACCESS ? true : false,
    name = filterByName,
    pageNum = page,
    rowsPerPageArg = rowsPerPage,
    accessTypeFilterValue,
  } = { }) => {
    const filterByAccessType = accessTypeFilterValue === PRODUCT_ACCESS_TYPE_ALL ? '' : accessTypeFilterValue;
    fetchList({
      apiUuid,
      filterByName : name,
      filterByAccessType,
      hasAccess,
      page: pageNum,
      rowsPerPage: rowsPerPageArg,
    });
  };
  const onFilterByNameKeyPress = (e) => {
    if (e.key === 'Enter') {
      setPage(0);
      fetchListWithFilters();
    }
  };
  const onChangePage = (newPage) => {
    if (page === newPage) { return; }
    setPage(newPage);
    fetchListWithFilters({ pageNum: newPage });
  };
  const onChangePreviousPage = () => { onChangePage(page - 1); };
  const onChangeNextPage = () => { onChangePage(page + 1); };

  const onChangeRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchListWithFilters({ pageNum: 0, rowsPerPageArg : newRowsPerPage });
  };

  const addButtonLabel = getI18nMessage('label.api.details.apiproducts.list.add.button');
  const handleAddButton = () => {
    setChangeMode(CHANGE_MODE_ADD_ACCESS);
  };
  const showAddBtn = hasUpdate && (changeMode === CHANGE_MODE_REMOVE_ACCESS);
  const addBtnFunc = showAddBtn ? handleAddButton : emptyFunction;

  const saveUpdatedRows = (forceRemove = false) => {
    if (changeMode === CHANGE_MODE_REMOVE_ACCESS) {
      const removedAPIProducts = currentProductsRows.filter(item => item.productApiAccessStatus === 'NOT_ASSIGNED');
      const data = map(removedAPIProducts,
          item => ({ productUuid: item.productUuid }),
        );
      updateAccess({
        apiUuid,
        action: 'remove',
        data,
        forceRemove,
      });
    } else if (changeMode === CHANGE_MODE_ADD_ACCESS) {
      const addededAPIProducts = currentProductsRows.filter(item => item.productApiAccessStatus === 'ASSIGNED');
      const data = map(addededAPIProducts,
          item => ({ productUuid: item.productUuid }),
        );
      updateAccess({
        apiUuid,
        data,
      });
    }
    if(isDialogOpen) {
      setIsDialogOpen(false)
    }
  };

  const onCancel = () => {
    if ((changeMode === CHANGE_MODE_ADD_ACCESS)) {
      setChangeMode(CHANGE_MODE_REMOVE_ACCESS);
    }
    setModifiedRecords(0);
    fetchListWithFilters();
  }

  const assistText = isFormDirty ? (<span>
    {getI18nMessage('label.api.details.apiproducts.list.save.assist.text', { num: modifiedRecords })}
    <WarningIcon className={classes.warningIcon} />
    {getI18nMessage('label.redepolyment.required')}
  </span>) : '';

  const onFilterByAccessType = (value) => {
    setAccessTypeFilterValue(value);
    setPage(0);
    fetchListWithFilters({ accessTypeFilterValue : value });
  }

  const ForceRemoveProductDialog = () => {
    return (<>
      <Typography>
        {getI18nMessage('label.product.apis.alert.dialog.force.remove.apis.description1', { entity : "Product" })}
      </Typography>
      <Typography/>
      <Typography>
        {getI18nMessage('label.product.apis.alert.dialog.force.remove.apis.description2')}
      </Typography>
    </>)
  }

  return (
    <Fragment>
      <ListContainer
        hasPaginationDisabled={isFormDirty}
        listPageId="products-list"
        isLoading={false}
        notificationId="request-notifications"
        notificationStatus={''}
        setNotificationStatus={() => {}}
        notificationMessage={''}
        setNotificationMessage={() => {}}
        pageHeaderTitle={''}
        pageHeaderDescription={''}
        pageHeaderTooltipTitle={getI18nMessage('label.request.list.page.title.help')}
        addButtonLabel={''}
        onAdd={false}
        showListHeader={true}
        filterAndSortContent={(
          <Fragment>
            <FilterByName
              fieldContainerClass={classes.nameFieldContainer}
              handleChange={setFilterByName}
              name={getI18nMessage('label.filter')}
              onKeyPress={onFilterByNameKeyPress}
              placeholder={getI18nMessage('label.product')}
              value={filterByName}
            />
            <FilterByAccessType
              fieldContainerClass={classes.accessTypeFilterContainer}
              data={PRODUCT_LIST_ACCESS_TYPE_FILTER_OPTIONS}
              name={getI18nMessage('label.filter')}
              value={accessTypeFilterValue}
              handleChange={onFilterByAccessType}
              hideLabel
            />
            <Grid item md={4} sm={4} xs={6}>
              {showAddBtn && <Button
                className={classes.addBtn}
                data-apim-test={'api-products-add'}
                data-layer7-test={'api-products-add'}
                disabled={isFormDirty}
                id={'api-products-add'}
                onClick={addBtnFunc}
                variant="contained" color="secondary"
              >
                {addButtonLabel}
              </Button>}
            </Grid>
          </Fragment>
        )}
        columns={getListColumns({ getI18nMessage, classes })}
        rows={currentProductsRows}
        noResultsMessage={getI18nMessage('label.no.results')}
        page={page}
        totalElements={totalElements}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={GRID_ROWS_PER_PAGE_OPTIONS}
        onChangeRowsPerPage={onChangeRowsPerPage}
        onChangePage={onChangePage}
        onChangePreviousPage={onChangePreviousPage}
        onChangeNextPage={onChangeNextPage}
        notifyMessages={emptyFunction}
        pageFilterAndSortClass={classes.pageFilterAndSortClass}
        pageBodyClass={classes.pageBodyClass}
      />
      {showFormActionBtns && <FormActionButtons
        assistText={assistText}
        isDisabled={!isFormDirty}
        nextText={'Save'}
        onCancelClick={onCancel}
        onNextClick={() => saveUpdatedRows(false)}
        id="app-api-products-form-buttons"
        isContainerFooter={true}
      />}
      <AlertDialog
        isOpen={isDialogOpen}
        containerClass={classes.alertDialogContainerClass}
        submitButtonClass={classes.alertDialogSubmitButtonClass}
        title={getI18nMessage('label.product.apis.alert.dialog.force.remove.apis.title')}
        description={''}
        onClose={()=> setIsDialogOpen(false)}
        onSubmit={()=> saveUpdatedRows(true)}
        onCancel={()=> setIsDialogOpen(false)}
        dialogId={'api-details-product-force-remove-dialog'}
        submitText={getI18nMessage('label.remove.product')}
        cancelText={getI18nMessage('label.cancel.button')}
        component={<ForceRemoveProductDialog/>}
      />
    </Fragment>
  );
};

const mapStateToProps = (state) => ({
  rows: getProductListResults(state),
  totalElements: getProductListTotalElements(state),
  totalPages: getProductListTotalPages(state),
  isApiUpdateSuccess: getIsProductAccessSaveSuccess(state),
  errors : getErrors(state),
});

Products.propTypes = {
  apiUuid: string,
  apiDetails: object,
  classes: shape({}),
  fetchList: func,
  isApiUpdateSuccess: bool,
  notifyMessages: func,
  rows: arrayOf(shape({})),
  totalElements: number,
  totalPages: number,
  updateAccess: func,
  userContext: object,
  tabValue: string,
  errors : arrayOf(shape({})),
};
Products.displayName = 'Products';

const mapDispatchToProps = {
  fetchList: fetchApisProductList,
  updateAccess: updateApisProductList,
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(Products);
